import React, { useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
  Chip,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Igual que entregado.php: catálogo de accesorios del servicio (equipo_accesorio,
// filtrado por servicio.relacion y por ciudad = sucursal del usuario, para no
// mezclar el stock de otra sucursal), con cantidad en almacén y un input de
// cantidad a asignar por fila. Al guardar, resta del stock (buen_estado) e
// inserta en historial_accesorio. No es serializado ni paginado (catálogo chico
// por sucursal, igual que el PHP viejo, que traía todo de una).
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };

const EntregaAccesoriosOT = ({ cod_ot, cod_cliente, servicio }) => {
  const { user } = useAuth();
  const [accesorios, setAccesorios] = useState([]);
  const [entregados, setEntregados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cantidades, setCantidades] = useState({}); // { [id]: cantidad }
  const [guardando, setGuardando] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errores, setErrores] = useState([]);

  const cargar = () => {
    setLoading(true);
    Promise.all([
      axios.get("/api/equipos-accesorio", { params: { servicio, ciudad: user.sucursal } }),
      axios.get("/api/historial-accesorio", { params: { cod_ot } }),
    ])
      .then(([resAccesorios, resHistorial]) => {
        setAccesorios(resAccesorios.data || []);
        setEntregados(resHistorial.data || []);
      })
      .catch((err) => console.error("Error al cargar accesorios", err))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [cod_ot, servicio, user.sucursal]);

  const setCantidad = (id, valor) => {
    setCantidades((prev) => ({ ...prev, [id]: valor }));
  };

  const seleccionados = Object.entries(cantidades).filter(([, v]) => Number(v) > 0);

  const guardar = async () => {
    if (!seleccionados.length) return;
    setGuardando(true);
    setErrores([]);
    const fallidos = [];
    const exitosos = [];

    for (const [idStr, cantidadStr] of seleccionados) {
      const id = Number(idStr);
      const cantidad = Number(cantidadStr);
      const accesorio = accesorios.find((a) => a.id === id);
      if (!accesorio) continue;

      try {
        await axios.post(`/api/equipos-accesorio/${id}/entregar`, {
          cantidad,
          cod_ot,
          asignado: cod_cliente,
          ciudad: user.sucursal,
          observacion: "Adicion de Accesorios al servicio",
          usu: user.username,
        });
        exitosos.push(idStr);
      } catch (err) {
        fallidos.push(err?.response?.data?.error || `Error al entregar "${accesorio.modelo}"`);
      }
    }

    setCantidades((prev) => {
      const next = { ...prev };
      exitosos.forEach((idStr) => delete next[idStr]);
      return next;
    });
    setErrores(fallidos);
    if (exitosos.length) setOpenSnackbar(true);
    cargar();
    setGuardando(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {!!entregados.length && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            Accesorios entregados
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 180, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Cantidad</TableCell>
                  <TableCell sx={headCellSx}>Fecha</TableCell>
                  <TableCell sx={headCellSx}>Observación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entregados.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{h.modelo}</TableCell>
                    <TableCell>{h.cantidad}</TableCell>
                    <TableCell>{String(h.fecha || "").slice(0, 10)}</TableCell>
                    <TableCell>{h.observacion}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Adicionar Accesorios
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!seleccionados.length || guardando}
          onClick={guardar}
          sx={{ minHeight: 26, height: 26, fontSize: 11 }}
        >
          {guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar Entrega"}
        </Button>
      </Stack>

      {!!errores.length && (
        <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
          {errores.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </MuiAlert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0.5 }}>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={headCellSx}>Disponible (Laboratorio)</TableCell>
              <TableCell sx={headCellSx}>Cantidad a Asignar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!accesorios.length ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay accesorios registrados para el servicio "{servicio}".
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              accesorios.map((a) => {
                const stockBajo = Number(a.buen_estado) < 20;
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.modelo}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={a.buen_estado}
                        color={stockBajo ? "warning" : "default"}
                        variant={stockBajo ? "filled" : "outlined"}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        type="number"
                        value={cantidades[a.id] ?? ""}
                        onChange={(e) => setCantidad(a.id, e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 70, "& .MuiInputBase-input": { fontSize: 11, py: 0.2 } }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Accesorios entregados correctamente.
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default EntregaAccesoriosOT;
